import type { ReactNode } from "react";
import { Alert, Box, Button, Container } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";

export interface ErrorStateProps {
  message: ReactNode;
  /** Refetch callback — never a page reload. Omit to render without retry. */
  onRetry?: () => void;
  retryLabel?: string;
  /** "inline" = Alert with action button (list/section errors);
   *  "panel" = centered page-level failure with a contained retry button. */
  variant?: "inline" | "panel";
}

/** The app's error-state archetype. Retry always means "refetch", not "reload". */
export function ErrorState({
  message,
  onRetry,
  retryLabel = "Try Again",
  variant = "inline",
}: ErrorStateProps) {
  if (variant === "panel") {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
          {message}
        </Alert>
        {onRetry && (
          <Button
            variant="contained"
            onClick={onRetry}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {retryLabel}
          </Button>
        )}
      </Container>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="error"
        action={
          onRetry && (
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
              {retryLabel}
            </Button>
          )
        }
      >
        {message}
      </Alert>
    </Box>
  );
}
